import axios from 'axios';

import i18n from '../app/i18n';

/**
 * Axios client for the Laravel API (§9). Base URL is `/api/v1`.
 *
 * In dev, Vite proxies `/api` to the Laravel container (see vite.config.ts) so the SPA
 * and API share an origin. That matters beyond convenience: admin auth is Sanctum
 * *cookie session* (§10.2), which requires same-origin + CSRF rather than a bearer
 * token. Getting this wrong only surfaces at the admin login screen, so the dev setup
 * mirrors production's same-origin arrangement from the start.
 */

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export const api = axios.create({
  baseURL: API_BASE_URL,
  // Send the Sanctum session cookie. Harmless for the public read-only API (§9.1),
  // required for the admin API (§9.2).
  withCredentials: true,
  // Attach the XSRF-TOKEN cookie as the X-XSRF-TOKEN header on mutating requests — the
  // other half of Sanctum's CSRF protection for the cookie-session admin (§10.2). Axios
  // 1.x needs this flag explicitly once credentials are enabled.
  withXSRFToken: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

/**
 * Every request carries the active UI locale, so content comes back in the language the
 * chrome is already showing (§9 conventions: `?locale=en|ka`).
 */
api.interceptors.request.use((config) => {
  config.params = { locale: i18n.language, ...(config.params ?? {}) };
  return config;
});

export type HealthResponse = {
  status: string;
  version: string;
};

export const fetchHealth = async (): Promise<HealthResponse> => {
  const { data } = await api.get<HealthResponse>('/health');
  return data;
};
